import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { EVENTS_PATH } from "~produce/production/events/helpers/eventDefinitionsSsrmRow";

export const MATERIAL_EVENT_DETAIL_TARGET_KIND = "materialEventDetail";

export const SPACE_EVENT_DETAIL_TARGET_KIND = "spaceEventDetail";

export const TASK_EVENT_DETAIL_TARGET_KIND = "taskEventDetail";

export const EVENT_DETAIL_TARGET_KINDS = [
	MATERIAL_EVENT_DETAIL_TARGET_KIND,
	SPACE_EVENT_DETAIL_TARGET_KIND,
	TASK_EVENT_DETAIL_TARGET_KIND
];

const ROUTE_SEGMENT_BY_TARGET_KIND: Record<string, string> = {
	[MATERIAL_EVENT_DETAIL_TARGET_KIND]: "material",
	[SPACE_EVENT_DETAIL_TARGET_KIND]: "space",
	[TASK_EVENT_DETAIL_TARGET_KIND]: "task"
};

const TARGET_KIND_BY_ROUTE_SEGMENT: Record<string, string> = {
	material: MATERIAL_EVENT_DETAIL_TARGET_KIND,
	space: SPACE_EVENT_DETAIL_TARGET_KIND,
	task: TASK_EVENT_DETAIL_TARGET_KIND
};

const EVENT_DETAIL_ROUTE_PATTERN =
	/^\/produce\/production\/events\/(material|space|task)\/([^/?#]+)\/details\/([^/?#]+)$/;

export interface EventDetailTarget {
	detailId: string;
	eventId: string;
}

export const eventDetailTargetId = (eventId: string, detailId: string): string => `${eventId}/${detailId}`;

export const parseEventDetailTargetId = (targetId: string): EventDetailTarget | null => {
	const [eventId, detailId, ...rest] = targetId.split("/");
	return eventId && detailId && rest.length === 0 ? { detailId, eventId } : null;
};

export const matchEventDetailReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = EVENT_DETAIL_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return {
			targetId: eventDetailTargetId(decodeURIComponent(match[2]), decodeURIComponent(match[3])),
			targetKind: TARGET_KIND_BY_ROUTE_SEGMENT[match[1]]
		};
	} catch {
		return null;
	}
};

const EVENT_TAB_VALUE_BY_TARGET_KIND: Record<string, string> = {
	[MATERIAL_EVENT_DETAIL_TARGET_KIND]: "1",
	[SPACE_EVENT_DETAIL_TARGET_KIND]: "3",
	[TASK_EVENT_DETAIL_TARGET_KIND]: "2"
};

export const eventTabValueForRoute = (pathname: string): string | undefined => {
	const targetKind = matchEventDetailReceiverRoute(pathname)?.targetKind;
	return targetKind ? EVENT_TAB_VALUE_BY_TARGET_KIND[targetKind] : undefined;
};

export const buildEventDetailPath = ({ targetId, targetKind }: ContinuationTarget): string => {
	const target = parseEventDetailTargetId(targetId);
	const segment = ROUTE_SEGMENT_BY_TARGET_KIND[targetKind];
	if (!target || !segment) return "";
	return `${EVENTS_PATH}/${segment}/${encodeURIComponent(target.eventId)}/details/${encodeURIComponent(target.detailId)}`;
};

const eventDetailReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: buildEventDetailPath,
	matchesRoute: matchEventDetailReceiverRoute
};

export default eventDetailReceiverIdentityAdapter;
